/**
 * Canvas renderers for the agent orb. Five shapes draw into an offscreen
 * buffer; the buffer is composited into a circular clip so the soften blur
 * never fades the circle's edge. Ported from the portal's appearance
 * renderer (`portal/components/fa/agent-detail/appearance/renderer.ts`);
 * the one delta is soften scaling by canvas size (see SOFTEN_REFERENCE_SIZE).
 */
import { expandColors, hexToHsl, rgba, SOFTEN_REFERENCE_SIZE, } from "./appearance.js";
const SHAPE_RENDERERS = {
    /** 14x14 grid; cells shift colour and alpha on layered sines. */
    pixel(ctx, w, h, t, cols) {
        const N = 14;
        const cell = w / N;
        for (let gy = 0; gy < N; gy++) {
            for (let gx = 0; gx < N; gx++) {
                const v = (Math.sin(gx * 0.55 + t) +
                    Math.cos(gy * 0.55 - t * 0.8) +
                    Math.sin((gx + gy) * 0.4 + t * 0.5)) /
                    3;
                const u = (v + 1) / 2;
                ctx.fillStyle = cols[Math.floor(u * cols.length) % cols.length];
                ctx.globalAlpha = 0.5 + 0.5 * u;
                ctx.fillRect(gx * cell, gy * cell, cell + 1, cell + 1);
            }
        }
        ctx.globalAlpha = 1;
    },
    /** Soft radial blobs on a two-frequency eased orbit with breathing radii. */
    flow(ctx, w, h, t, cols) {
        ctx.fillStyle = cols[0];
        ctx.fillRect(0, 0, w, h);
        ctx.filter = `blur(${w * 0.05}px)`;
        const n = Math.max(4, cols.length * 2);
        for (let i = 0; i < n; i++) {
            const c = cols[i % cols.length];
            const px = Math.sin(t * 0.4 + i * 1.3) + 0.5 * Math.sin(t * 0.9 + i * 2.1);
            const py = Math.cos(t * 0.35 + i * 1.7) + 0.5 * Math.cos(t * 0.8 + i * 1.1);
            const x = w / 2 + px * w * 0.22;
            const y = h / 2 + py * h * 0.22;
            const r = w * (0.3 + 0.06 * Math.sin(t * 0.6 + i));
            const g = ctx.createRadialGradient(x, y, 0, x, y, r);
            g.addColorStop(0, rgba(c, 0.85));
            g.addColorStop(1, rgba(c, 0));
            ctx.fillStyle = g;
            ctx.beginPath();
            ctx.arc(x, y, r, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.filter = "none";
    },
    /** Radial bloom, bright core to dark edge, core pulsing in and out. */
    radio(ctx, w, h, t, cols) {
        const ord = [...cols].sort((a, b) => hexToHsl(b)[2] - hexToHsl(a)[2]);
        const n = ord.length;
        const cx = w / 2 + Math.sin(t * 0.5) * w * 0.03;
        const cy = h / 2 + Math.cos(t * 0.4) * h * 0.03;
        const pulse = 0.85 + 0.2 * Math.sin(t * 0.9);
        const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, w * 0.72);
        for (let i = 0; i < n; i++) {
            const p = Math.min(1, Math.pow(n === 1 ? 0 : i / (n - 1), 1.4) * pulse);
            g.addColorStop(p, ord[i]);
        }
        g.addColorStop(1, ord[n - 1]);
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
    },
    /** Aurora: vertical colour wash plus wavy curtains travelling sideways. */
    stripes(ctx, w, h, t, cols) {
        const n = cols.length;
        const g = ctx.createLinearGradient(0, 0, 0, h);
        for (let i = 0; i < n; i++)
            g.addColorStop(n === 1 ? 0.5 : i / (n - 1), cols[i]);
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
        ctx.filter = `blur(${w * 0.05}px)`;
        const layers = Math.max(3, n + 1);
        const thick = h * 0.2;
        const amp = h * 0.055;
        const steps = 24;
        for (let i = 0; i < layers; i++) {
            const baseY = h * ((i + 0.5) / layers) + Math.sin(t * 0.4 + i) * h * 0.06;
            ctx.fillStyle = rgba(cols[i % n], 0.45);
            ctx.beginPath();
            for (let s = 0; s <= steps; s++) {
                const x = (s / steps) * w;
                ctx.lineTo(x, baseY + Math.sin(x * 0.012 + t * 0.9 + i * 0.7) * amp);
            }
            for (let s = steps; s >= 0; s--) {
                const x = (s / steps) * w;
                ctx.lineTo(x, baseY + thick + Math.sin(x * 0.012 + t * 0.9 + i * 0.7 + 0.5) * amp);
            }
            ctx.closePath();
            ctx.fill();
        }
        ctx.filter = "none";
    },
    /** Conic sweep with pendulum-eased angular velocity and a drifting centre. */
    conic(ctx, w, h, t, cols) {
        const ang = t * 0.5 + 0.35 * Math.sin(t * 0.6);
        const cx = w / 2 + Math.sin(t * 0.5) * w * 0.04;
        const cy = h / 2 + Math.cos(t * 0.43) * h * 0.04;
        const n = cols.length;
        // createConicGradient is unavailable in older Safari and in jsdom.
        if (typeof ctx.createConicGradient !== "function") {
            const g = ctx.createLinearGradient(0, 0, w, h);
            for (let i = 0; i < n; i++)
                g.addColorStop(n === 1 ? 0.5 : i / (n - 1), cols[i]);
            ctx.fillStyle = g;
            ctx.fillRect(0, 0, w, h);
            return;
        }
        const g = ctx.createConicGradient(ang, cx, cy);
        for (let i = 0; i <= n; i++)
            g.addColorStop(i / n, cols[i % n]);
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
    },
};
/** 128px grain tile, baked once per document and reused by every orb. */
let noiseTile = null;
function getNoiseTile() {
    if (noiseTile)
        return noiseTile;
    if (typeof document === "undefined")
        return null;
    const size = 128;
    const cv = document.createElement("canvas");
    cv.width = size;
    cv.height = size;
    const cx = cv.getContext("2d");
    if (!cx)
        return null;
    const img = cx.createImageData(size, size);
    for (let i = 0; i < img.data.length; i += 4) {
        const v = 110 + Math.random() * 90;
        img.data[i] = img.data[i + 1] = img.data[i + 2] = v;
        img.data[i + 3] = 255;
    }
    cx.putImageData(img, 0, 0);
    noiseTile = cv;
    return cv;
}
/**
 * Binds a canvas to a reusable offscreen buffer. The caller owns the clock and
 * the loop, so a reduced-motion consumer can simply `draw(0, config)` once.
 * Returns null when a 2d context is unavailable (jsdom, exotic embeds).
 */
export function createOrbEngine(canvas, { texture = true } = {}) {
    const ctx = canvas.getContext("2d");
    if (!ctx)
        return null;
    const buf = document.createElement("canvas");
    const bctx = buf.getContext("2d");
    if (!bctx)
        return null;
    let cw = 0;
    let ch = 0;
    return {
        draw(t, config) {
            const w = canvas.clientWidth;
            const h = canvas.clientHeight;
            // display:none keeps the last good pixels rather than zeroing the store.
            if (w === 0 || h === 0)
                return;
            const dpr = Math.min(2, (typeof window !== "undefined" && window.devicePixelRatio) || 1);
            if (w !== cw || h !== ch) {
                cw = w;
                ch = h;
                canvas.width = buf.width = Math.round(w * dpr);
                canvas.height = buf.height = Math.round(h * dpr);
            }
            const cols = expandColors(config.colors);
            bctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            bctx.clearRect(0, 0, w, h);
            (SHAPE_RENDERERS[config.shape] ?? SHAPE_RENDERERS.flow)(bctx, w, h, t, cols);
            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.save();
            ctx.beginPath();
            ctx.arc(canvas.width / 2, canvas.height / 2, canvas.width / 2, 0, Math.PI * 2);
            ctx.clip();
            // Overscan by the blur radius so the softened edge stays off-canvas and
            // the circular clip keeps a crisp rim. Soften is authored against the
            // portal's 320px stage; scale it to this canvas's size.
            const soft = (texture ? config.soften * (w / SOFTEN_REFERENCE_SIZE) : 0) * dpr;
            ctx.filter = soft > 0 ? `blur(${soft}px)` : "none";
            ctx.drawImage(buf, -soft, -soft, canvas.width + soft * 2, canvas.height + soft * 2);
            ctx.filter = "none";
            const noise = texture ? config.noise : 0;
            const tile = noise > 0 ? getNoiseTile() : null;
            if (tile) {
                ctx.globalAlpha = Math.min(0.6, noise * 0.6);
                ctx.globalCompositeOperation = "overlay";
                const step = tile.width * 2;
                for (let y = 0; y < canvas.height; y += step) {
                    for (let x = 0; x < canvas.width; x += step)
                        ctx.drawImage(tile, x, y, step, step);
                }
                ctx.globalAlpha = 1;
                ctx.globalCompositeOperation = "source-over";
            }
            ctx.restore();
        },
    };
}
